import { OrbitControls } from "@react-three/drei";
import Camera from "@components/Camera";
import { useCallback, useContext, useEffect, useRef, useState } from "react";
import { Vector3 } from "three";
import { useFrame } from "@react-three/fiber";
import { easings, useSpring } from "react-spring";
import { OrbitControls as OrbitControlsType } from "three-stdlib";
import { getMax, getMin, getValue, MinMaxValue } from "@utils/MinMaxValue";
import { AppData, Spring } from "@App";
import { EScenario } from "@utils/Scenarios";
import { useViewportData } from "@components/ViewportManager";
import { MathUtils } from "@utils/MathUtils";
import { DeviceProps } from "@components/Root";
import { InteractContext } from "@components/Interaction";

type CameraSettings = {
    targetX: number;
    targetY: number;
    targetZ: number;
    azimuth: MinMaxValue;
    polar: MinMaxValue;
    distance: MinMaxValue;
    rotateSpeed: number;
}
const cameraSettings: Record<EScenario, CameraSettings> = {
    [EScenario.Default]: {
        targetX: 0,
        targetY: 0.5,
        targetZ: 0,
        azimuth: [-Math.PI * 0.2, -Infinity, Infinity],
        polar: [Math.PI * 0.3625, 0.1, Math.PI * 0.5 - 0.1],
        distance: [4, 3.5, 5.5],
        rotateSpeed: 0.3,
    },
    [EScenario.Home]: {
        targetX: 0,
        targetY: 0.25,
        targetZ: 0,
        azimuth: [-Math.PI * 0.05, -Infinity, Infinity],
        polar: [Math.PI * 0.4, 0.1, Math.PI * 0.5 - 0.1],
        distance: [4, 3.5, 5.5],
        rotateSpeed: 0.3,
    },
    [EScenario.Home2]: {
        targetX: 0,
        targetY: 2,
        targetZ: 2,
        azimuth: [-Math.PI * 0.4, -Math.PI * 0.45, -Math.PI * 0.2],
        polar: [Math.PI * 0.46, Math.PI * 0.4, Math.PI * 0.5 - 0.1],
        distance: [8, 1],
        rotateSpeed: 0.1,
    },
    [EScenario.Explode]: {
        targetX: 0,
        targetY: 2,
        targetZ: 0,
        azimuth: [-Math.PI * 0.4, -Math.PI * 0.45, -Math.PI * 0.2],
        polar: [Math.PI * 0.46, Math.PI * 0.4, Math.PI * 0.5 - 0.1],
        distance: [8, 1],
        rotateSpeed: 0.1,
    },
    [EScenario.Temperature]: {
        targetX: 1,
        targetY: 0.75,
        targetZ: 0,
        azimuth: [-Math.PI * 0.1, 0.05],
        polar: [Math.PI * 0.45, 0.1],
        distance: [6.5, 1],
        rotateSpeed: 0.04,
    },
    [EScenario.Metrics]: {
        targetX: 0,
        targetY: 0.5,
        targetZ: 0,
        azimuth: [-Math.PI * 0.01, 0.1],
        polar: [Math.PI * 0.46, 0.1],
        distance: [4.5, 0.05],
        rotateSpeed: 0.04,
    }
}

const initialCameraSpring: Spring = { easing: easings.easeInOutCubic, duration: 2000 };
const cameraSpring: Spring = { tension: 100, friction: 100, mass: 30 };
const precise: Spring = { precision: 1e-7 };

const offset = new Vector3(0, 0, 1);
export function ControlledCamera({ mobile }: DeviceProps) {
    const { setInteracted } = useContext(InteractContext);

    const { scenario, prevScenario } = useViewportData<AppData>("main")!;

    const distanceMultiplier = mobile ? scenario === EScenario.Metrics ? 2.2 : 1.8 : 1;

    const controls = useRef<OrbitControlsType>(null);

    // Initialize the position during the first render frame
    const [init, setInit] = useState(false);

    // The position state, used for interpolation
    const [state, setState] = useState({
        targetX: cameraSettings[EScenario.Default].targetX,
        targetY: cameraSettings[EScenario.Default].targetY,
        targetZ: cameraSettings[EScenario.Default].targetZ,
        azimuth: getValue(cameraSettings[EScenario.Default].azimuth),
        polar: getValue(cameraSettings[EScenario.Default].polar),
        distance: getValue(cameraSettings[EScenario.Default].distance) * distanceMultiplier
    });

    // Save the current position state
    const saveState = useCallback(() => {
        if (!controls.current) return;
        setState({
            targetX: controls.current.target.x,
            targetY: controls.current.target.y,
            targetZ: controls.current.target.z,
            azimuth: controls.current.getAzimuthalAngle(),
            polar: controls.current.getPolarAngle(),
            distance: controls.current.getDistance()
        });
    }, [controls, setState]);

    // During the animation, damping must be off and constraints must be lifted
    // After the animation, re-enable constraints and apply immediately before enabling damping
    const [cameraAnimating, setCameraAnimating] = useState(false);
    const onStartAnimation = useCallback(() => {
        saveState();
        setCameraAnimating(true);
        if (controls.current) {
            controls.current.enableDamping = false;
            controls.current.minAzimuthAngle = -Infinity;
            controls.current.maxAzimuthAngle = Infinity;
            controls.current.minPolarAngle = -Infinity;
            controls.current.maxPolarAngle = Infinity;
            controls.current.minDistance = 0;
            controls.current.maxDistance = Infinity;
        }
    }, [controls, saveState, setCameraAnimating]);
    const onEndAnimation = useCallback(() => {
        setCameraAnimating(false);
        if (controls.current) {
            controls.current.minAzimuthAngle = getMin(cameraSettings[scenario].azimuth);
            controls.current.maxAzimuthAngle = getMax(cameraSettings[scenario].azimuth);
            controls.current.minPolarAngle = getMin(cameraSettings[scenario].polar);
            controls.current.maxPolarAngle = getMax(cameraSettings[scenario].polar);
            controls.current.minDistance = getMin(cameraSettings[scenario].distance) * distanceMultiplier;
            controls.current.maxDistance = getMax(cameraSettings[scenario].distance) * distanceMultiplier;
            controls.current.update();
            controls.current.enableDamping = true;
            controls.current.rotateSpeed = cameraSettings[scenario].rotateSpeed;
        }
    }, [controls, scenario, setCameraAnimating, distanceMultiplier]);

    const animateCamera = useCallback((t: number) => {
        if (!controls.current) return;

        // Lerp the Target X and Y 
        controls.current.target.set(
            MathUtils.lerp(
                state.targetX,
                cameraSettings[scenario].targetX,
                t
            ),
            MathUtils.lerp(
                state.targetY,
                cameraSettings[scenario].targetY,
                t
            ),
            MathUtils.lerp(
                state.targetZ,
                !mobile && scenario === EScenario.Explode ? 1 : cameraSettings[scenario].targetZ,
                t
            )
        );

        // Lerp the Azimuthal angle
        controls.current.setAzimuthalAngle(MathUtils.lerp(
            state.azimuth,
            getValue(cameraSettings[scenario].azimuth),
            t
        ));

        // Lerp the Polar angle
        controls.current.setPolarAngle(MathUtils.lerp(
            state.polar,
            getValue(cameraSettings[scenario].polar),
            t
        ));

        // Apply angles to get the current position for distance calculation
        controls.current.update();
        controls.current.object.updateMatrixWorld();

        // Lerp the distance
        const dist = MathUtils.lerp(
            state.distance,
            getValue(cameraSettings[scenario].distance) * distanceMultiplier,
            t
        );

        // Apply the distance from the target along the current look vector
        offset.set(0, 0, dist).applyQuaternion(controls.current.object.quaternion);
        controls.current.object.position.copy(offset.add(controls.current.target));
    }, [scenario, controls, state.targetX, state.targetY, state.targetZ, state.azimuth, state.polar, state.distance, mobile, distanceMultiplier]);

    // Control the animation with a non-precise spring, so that user-controls can resume quickly.
    // Always reset the spring when the scenario changes, as the animation assumes time always goes from 0 to 1.
    // This allows us to resume the animation from the current position (saved state)
    const spring = prevScenario === EScenario.Default ? initialCameraSpring : cameraSpring;
    useSpring({ from: { value: init ? 0 : 1 }, to: { value: 1 }, reset: true, onStart: onStartAnimation, onRest: onEndAnimation, config: spring }, [scenario, mobile]);
    // The values use a precise spring to avoid visible jumps at the end. 
    // The result is that the animation stops at around 99.9%.
    const [{ value: cameraTime }] = useSpring({ from: { value: init ? 0 : 1 }, to: { value: 1 }, reset: true, config: { ...spring, ...precise } }, [scenario, mobile]);

    // Save the state when the scenario changes, so the animation can play from there.
    useEffect(() => {
        saveState();
    }, [scenario, mobile, saveState]);

    useFrame(() => {
        if (controls.current && !init) {
            // Global control settings
            controls.current.dampingFactor = 0.025;
            controls.current.zoomSpeed = 0.2;
            controls.current.enablePan = false;

            // Initialize the position at the end of the animation
            onStartAnimation();
            animateCamera(1);
            onEndAnimation();
            setInit(true);
        } else if (cameraAnimating) {
            animateCamera(cameraTime.get());
        }
    });

    return (
        <>
            <Camera fov={50} position={[0, 2, 5]} near={0.01} far={30} />
            <OrbitControls ref={controls} target={[0, 0.5, 0]} enableDamping={false} onChange={() => setInteracted(true)} />
        </>
    );
}
